import {jsx as _jsx, jsxs as _jsxs} from "react/jsx-runtime"; function _optionalChain(ops) { let lastAccessLHS = undefined; let value = ops[0]; let i = 1; while (i < ops.length) { const op = ops[i]; const fn = ops[i + 1]; i += 2; if ((op === 'optionalAccess' || op === 'optionalCall') && value == null) { return undefined; } if (op === 'access' || op === 'optionalAccess') { lastAccessLHS = value; value = fn(value); } else if (op === 'call' || op === 'optionalCall') { value = fn((...args) => value.call(lastAccessLHS, ...args)); lastAccessLHS = undefined; } } return value; }import styled from '@emotion/styled'
import {Edit} from '@mui/icons-material'
import * as React from 'react'
import {forwardRef, useEffect, useRef, useState} from 'react'
import TextAreaAutoSize from 'react-textarea-autosize'
import {PALETTE} from '../styles/paletteV3'
import {FONT_FAMILY} from '../styles/typographyV2'


const StaticBlock = styled('div')(({disabled}) => ({
  alignItems: 'center',
  cursor: disabled ? 'default' : 'pointer',
  display: 'flex',
  fontFamily: FONT_FAMILY.SANS_SERIF,
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  outline: disabled ? 'none' : undefined,
  width: '100%',
  ':hover': {
    opacity: disabled ? undefined : 0.5
  }
}))

const Placeholder = styled('div')({
  color: PALETTE.SLATE_600
})

const StaticValue = styled('div')({
  color: PALETTE.SLATE_700
})

const Error = styled('div')({
  color: PALETTE.TOMATO_500,
  fontSize: 14
})

const StyledIcon = styled(Edit)({
  color: PALETTE.SLATE_600,
  height: 18,
  width: 18,
  marginLeft: 8
})

const Input = styled('input')({
  backgroundColor: 'transparent',
  border: 0,
  color: PALETTE.SLATE_700,
  display: 'block',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  outline: 'none',
  padding: 0,
  width: 'auto'
})

const TextArea = styled(TextAreaAutoSize)({
  backgroundColor: 'transparent',
  border: 0,
  color: PALETTE.SLATE_700,
  display: 'block',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  outline: 'none',
  padding: 0,
  resize: 'none',
  width: '100%'
})

const Form = styled('form')({
  border: 0,
  display: 'flex',
  flexDirection: 'column',
  fontSize: 'inherit',
  fontWeight: 'inherit',
  lineHeight: 'inherit',
  margin: 0,
  padding: 0
})
















const EditableText = forwardRef((props, ref) => {
  const {
    initialValue,
    error,
    handleSubmit,
    maxLength,
    placeholder,
    validate,
    autoFocus: autoFocusProp,
    className,
    disabled,
    hideIcon,
    isWrap,
    onEditingChange
  } = props
  const [isEditing, setIsEditing] = useState(false)
  const inputRef = useRef(null)
  const [autoFocus, setAutoFocus] = useState(autoFocusProp)
  const [value, setValue] = useState(initialValue)
  useEffect(() => {
    if (isEditing) return
    setValue(initialValue)
  }, [initialValue])

  const setEditing = (isEditing) => {
    setIsEditing(isEditing)
    setAutoFocus(false)
    _optionalChain([onEditingChange, 'optionalCall', _ => _(isEditing)])
  }

  const onChange = (e) => {
    const nextValue = e.target.value || ''
    validate(nextValue)
    // make sure this is always true
    // repro: remove all text, blur input, focus input (with error present), then type a char
    setEditing(true)
    setValue(nextValue)
  }

  const onFocus = (e) => {
    if (value.toLowerCase().startsWith(placeholder.toLowerCase())) {
      e.target.select()
    }
    if (isWrap) {
      // hack to move caret to end
      // because it's 2020 and we still have to do stupid hacks like this >:-(
      const tmp = e.target.value
      e.target.value = ''
      e.target.value = tmp
    }
  }

  const onSubmit = async (e) => {
    e.preventDefault()
    setEditing(false)
    if (value.trim() === initialValue.trim()) return
    handleSubmit(value)
  }

  const reset = () => {
    setEditing(false)
    setValue(initialValue)
    validate(initialValue)
    _optionalChain([inputRef, 'access', _2 => _2.current, 'optionalAccess', _3 => _3.blur, 'call', _4 => _4()])
  }

  const onKeyDown = (e) => {
    if (e.key === 'Escape') {
      // wait a tick so other escape listeners see that this is the active element
      setTimeout(reset)
    }
  }

  const showEditing = (error || isEditing || autoFocus) && !disabled
  if (showEditing) {
    const inProps = {
      autoFocus: true,
      ref: inputRef ,
      maxLength,
      onBlur: onSubmit,
      onChange: onChange,
      onFocus: onFocus,
      onKeyDown: onKeyDown,
      placeholder,
      value
    } 
    return (
      _jsx('div', { className: className, ref: ref, children: 
        _jsxs(Form, { onSubmit: onSubmit, children: [
          isWrap ? _jsx(TextArea, { ...inProps, maxRows: 3,} ) : _jsx(Input, { ...inProps,} )
          , error && _jsx(Error, { children: error})
        ]})
      })
    )
  }
  const showPlaceholder = !value && placeholder
  return (
    _jsx('div', { className: className, ref: ref, children: 
      _jsxs(StaticBlock, {
        disabled: disabled,
        tabIndex: 0,
        onFocus: () => setEditing(true),
        onClick: () => setEditing(true),
 children: [
        showPlaceholder && _jsx(Placeholder, { children: placeholder})
        , value && _jsx(StaticValue, { children: value})
        , !hideIcon && !disabled && _jsx(StyledIcon, {} )
      ]})
    })
  )
})
export default EditableText
